import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CryptoJS from "crypto-js"; // hashing phone number
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../../assets/RegistrationForm.css"; // styles
import AlertBox from "../AlertBox/AlertBox";
import { collectDeviceContext } from "../../utils/deviceContext";
import { getDeviceChecksSnapshot } from "../../utils/deviceChecks";
import { useTranslation, Trans } from "react-i18next";
import SiteHeader from "../SiteHeader";
import NetworkBanner from "../NetworkBanner";
import BrowserCheck from "../BrowserCheck";
import InfoToggles from "../InfoToggles";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";

const PID_STORAGE_KEY = "participantPid";

// ?pid from the link; kept for the tab session so a reload without the query
// string (or a redirect that dropped it) still has the ID.
function readPidFromLink() {
    if (typeof window === "undefined") return "";
    const fromQuery = (new URLSearchParams(window.location.search).get("pid") || "").trim();
    try {
        if (fromQuery) {
            window.sessionStorage.setItem(PID_STORAGE_KEY, fromQuery);
            return fromQuery;
        }
        return (window.sessionStorage.getItem(PID_STORAGE_KEY) || "").trim();
    } catch (e) {
        return fromQuery;
    }
}

function matchesPattern(pattern, value) {
    try { return new RegExp(pattern).test(value); } catch (e) { return /^[A-Za-z0-9_-]{3,32}$/.test(value); }
}

// TODO:: we could manage alert messages as a lookup object with a default
const DEFAULT_ALERT_ERROR_KEY = "register.defaultError";

const CLIENT_READY_POLL_INTERVAL = 5000;

const RegistrationForm = () => {
    const { t, i18n } = useTranslation();
    const { online } = useNetworkStatus();
    const pollFailuresRef = useRef(0);
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const { setIsValidDonor } = useAuth();
    const navigate = useNavigate();
    // fullPhone holds the complete E.164 digits (dialCode + local number) as
    // returned by react-phone-input-2, e.g. "14155552671" for +1 (415) 555-2671.
    const [fullPhone, setFullPhone] = useState("");
    // Participant ID (2026-10-21): issued by the study team; ?pid=<ID> on the
    // link pre-fills it so the same link works for repeat donations.
    const [pidFromLink] = useState(readPidFromLink);
    const [participantCode, setParticipantCode] = useState(pidFromLink);
    const [returning, setReturning] = useState(false);
    const [idRules, setIdRules] = useState({ pattern: "^[A-Za-z0-9_-]{3,32}$", example: "", hint: {} });
    // Locked only while the link's ID is valid; a mistyped link stays editable
    // instead of trapping the participant on an ID that can never pass.
    // When locked, the "enter your ID" step and the input are not shown.
    const pidLocked = Boolean(pidFromLink) && matchesPattern(idRules.pattern, pidFromLink);
    useEffect(() => {
        axios.get(new URL("/api/participant-id-rules", process.env.REACT_APP_BASE_URL), { timeout: 8000 })
            .then((r) => { if (r.data && r.data.pattern) setIdRules(r.data); })
            .catch(() => { /* defaults stay */ });
    }, []);
    const [isPhoneInputDisabled, setIsPhoneInputDisabled] = useState(false); // State to disable input after submit
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [imageUrl, setImageUrl] = useState("");
    const [validationMessage, setValidationMessage] = useState(""); // State for error message
    const [alert, setAlert] = useState(null);
    const [isLoadingQR, setIsLoadingQR] = useState(false);

    // Validate the full phone number (dialCode + local digits combined).
    // react-phone-input-2 returns the full number as a digit string (no +).
    // We just check it's within the ITU-T E.164 range of 7-15 total digits.
    const validatePhoneNumber = () => {
        const digits = fullPhone.replace(/\D/g, "");
        if (digits.length >= 7 && digits.length <= 15) {
            return digits;
        }
        return "";
    };
            
    const handleSubmit = async (event) => {
        event.preventDefault();
        const validatedNumber = validatePhoneNumber();
        const code = participantCode.trim();
        if (!matchesPattern(idRules.pattern, code)) {
            setValidationMessage(t("register.invalidParticipant"));
            setIsButtonDisabled(false);
            return;
        }

        if (validatedNumber) {
            setIsButtonDisabled(true);
            setIsPhoneInputDisabled(true);
            setIsLoadingQR(true);
            setAlert({level: "info", message: t("register.registering")})
            setValidationMessage("");

            // Hash the full E.164 digit string (dialCode + local number)
            const phoneNumberHash = CryptoJS.SHA256(validatedNumber).toString(CryptoJS.enc.Hex);

            try {
                const targetUrl = new URL("/api/register", baseUrl);
                const clientContext = await collectDeviceContext().catch(() => ({}));
                clientContext.extra = { device_checks: getDeviceChecksSnapshot() };
                const response = await axios.post(targetUrl, {
                    phoneNumberHash: phoneNumberHash,
                    participantCode: code,
                    clientContext,
                });
                const { qrCodeImageUrl } = response.data;
                setReturning(Boolean(response.data.returning));
                setImageUrl(qrCodeImageUrl);
            } catch (error) {
                setAlert({level: "warning", message: t(DEFAULT_ALERT_ERROR_KEY)})
                console.error("Registration failed:", error);
                setIsButtonDisabled(false);
            }
            finally {
                setIsLoadingQR(false);
            }
        } else {
            setValidationMessage(t("register.invalidPhone"));
            setIsButtonDisabled(false);
        }
    };

    // Poll backend until client is ready, then redirect
    useEffect(() => {
        let interval;

        if (imageUrl) {
            interval = setInterval(async () => {
                try {
                    const targetUrl = new URL("/api/client-ready", baseUrl);
                    const response = await axios.get(targetUrl);
                    if (response.data.ready) {
                        clearInterval(interval);
                        setIsValidDonor(true);
                        navigate("/chat-selections");
                    }
                } catch (error) {
                    // Transient / network failure (no HTTP response): keep polling —
                    // the phone-side link is unaffected by a browser blip (2026-10-06).
                    if (!error.response && pollFailuresRef.current < 60) { // ~5 minutes at 5s
                        pollFailuresRef.current += 1;
                        return;
                    }
                    // on a real error stop checking for ready status and alert user
                    clearInterval(interval);
                    setImageUrl("");

                    let error_message = error.response?.data?.error || error.message || t(DEFAULT_ALERT_ERROR_KEY);


                    if (error_message === "Your device is already registered with us") {
                        error_message = t("register.alreadyRegistered");
                    }

                    if (error_message === "Max qr code retries reached") {
                        setAlert({ level: "warning", message: t("register.maxQrRetries") });
                    }
                    else{
                        setAlert({level: "warning", message: error_message})
                    }
                    console.error("Failed to check client ready status:", error);
                }
            }, CLIENT_READY_POLL_INTERVAL); // Poll client-ready endpoint according to default interval
        }

        return () => clearInterval(interval);
    }, [baseUrl, imageUrl, navigate, setIsValidDonor, t]);


    return (
        <div className="appContainer">
            <SiteHeader />
            <div className="content" style={{ maxWidth: "800px", margin: "0 auto", paddingLeft: "20px", paddingRight: "20px" }}>
                <NetworkBanner retrying={!online} />
                {imageUrl && returning && (
                    <p style={{ background: "#eef7ef", border: "1px solid #cfe3d3", borderRadius: 8, padding: "10px 14px" }}>{t("register.returningNote")}</p>
                )}
                {!imageUrl && (
                    <div className="instructions-content">
                        <BrowserCheck />
                        <h2>{t("register.instructions")}</h2>
                        <ol className="qrStepsList" style={{
                            listStyle: "none",
                            padding: "0",
                            margin: "0"
                        }}>
                            {!pidLocked && (
                                <li className="qrStep" style={{
                                    display: "flex",
                                    gap: "15px",
                                    marginBottom: "16px",
                                    alignItems: "flex-start"
                                }}>
                                    <div style={{
                                        flexShrink: 0,
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        backgroundColor: "#e7f5ff",
                                        border: "2px solid #00b8e6",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                        color: "#00b8e6"
                                    }}>1</div>
                                    <div style={{ paddingTop: "4px" }}>
                                        {t("register.step0")}
                                    </div>
                                </li>
                            )}
                            <li className="qrStep" style={{
                                display: "flex",
                                gap: "15px",
                                marginBottom: "16px",
                                alignItems: "flex-start"
                            }}>
                                <div style={{
                                    flexShrink: 0,
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    backgroundColor: "#e7f5ff",
                                    border: "2px solid #00b8e6",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    color: "#00b8e6"
                                }}>{pidLocked ? 1 : 2}</div>
                                <div style={{ paddingTop: "4px" }}>
                                    {t("register.step1")}
                                </div>
                            </li>
                            <li className="qrStep" style={{
                                display: "flex",
                                gap: "15px",
                                marginBottom: "0",
                                alignItems: "flex-start"
                            }}>
                                <div style={{
                                    flexShrink: 0,
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    backgroundColor: "#e7f5ff",
                                    border: "2px solid #00b8e6",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    color: "#00b8e6"
                                }}>{pidLocked ? 2 : 3}</div>
                                <div style={{ paddingTop: "4px" }}>
                                    {t("register.step2")}
                                </div>
                            </li>
                        </ol>
                    </div>
                )}
                <div className="infoButtonContainer">
                    {!imageUrl && (
                        <form onSubmit={handleSubmit} className="reg-form">
                            <div className="reg-rows">
                                <label htmlFor={pidLocked ? undefined : "participant-code"} className="reg-label"><strong>{t("register.participantLabel")}</strong></label>
                                {pidLocked ? (
                                    <span id="participant-code" className="reg-pid-fixed">{pidFromLink}</span>
                                ) : (
                                    <input
                                        id="participant-code"
                                        name="participantCode"
                                        type="text"
                                        className="reg-input"
                                        value={participantCode}
                                        onChange={(e) => setParticipantCode(e.target.value)}
                                        disabled={isPhoneInputDisabled}
                                        placeholder={(idRules.hint && (idRules.hint[i18n.language] || idRules.hint[i18n.language.split("-")[0]])) || t("register.participantPlaceholder")}
                                        autoComplete="off"
                                        required
                                        maxLength={32}
                                    />
                                )}
                                <label htmlFor="phone-input" className="reg-label"><strong>{t("register.phoneLabel")}</strong></label>
                                <PhoneInput
                                    country={"br"}
                                    value={fullPhone}
                                    onChange={(value) => setFullPhone(value)}
                                    disabled={isPhoneInputDisabled}
                                    enableSearch={true}
                                    disableSearchIcon={true}
                                    searchPlaceholder={t("register.searchCountry")}
                                    inputProps={{
                                        required: true,
                                        name: "phone",
                                        id: "phone-input",
                                    }}
                                    containerClass="reg-phone"
                                    containerStyle={{ width: "320px", maxWidth: "100%" }}
                                    inputStyle={{ width: "100%", height: "38px", boxSizing: "border-box", padding: "8px 8px 8px 48px", fontSize: "15px" }}
                                    buttonStyle={{ height: "38px" }}
                                />
                                <span aria-hidden="true" />
                                <div className="reg-actions">
                                    <button
                                        className={isButtonDisabled ? "disabledButton" : "button"}
                                        disabled={isButtonDisabled}
                                        type="submit"
                                        style={{ marginBottom: "0" }}
                                    >
                                        {t("register.register")}
                                    </button>
                                </div>
                            </div>
                            {validationMessage && (
                                <div style={{ color: "red", marginBottom: "15px", fontSize: "14px" }}>
                                    {validationMessage}
                                </div>
                            )}
                            {alert && <AlertBox message={alert.message} level={alert.level} onClose={() => setAlert(null)} />}
                        </form>
                    )}
                    {!imageUrl && <InfoToggles />}

                    {isLoadingQR && (
                        <div className="spinner-container">
                            <div className="loading-spinner"></div>
                            <p>{t("register.generating")}</p>
                        </div>
                    )}

                    {imageUrl && (
                        <div className="qrCodeContainer" style={{ marginTop: "20px", paddingTop: "20px" }}>
                            <div className="qrCodeLayout" style={{
                                display: "flex",
                                gap: "40px",
                                alignItems: "flex-start",
                                justifyContent: "space-between"
                            }}>
                                {/* Left Column: Instructions */}
                                <div className="qrInstructionsSection" style={{
                                    flex: 1,
                                    minWidth: "0"
                                }}>
                                    <h3 style={{ marginTop: "0", marginBottom: "20px", fontSize: "18px" }}>
                                        {t("register.scanTitle")}
                                    </h3>
                                    <ol className="qrStepsList" style={{
                                        listStyle: "none",
                                        padding: "0",
                                        margin: "0"
                                    }}>
                                        <li className="qrStep" style={{
                                            display: "flex",
                                            gap: "15px",
                                            marginBottom: "16px",
                                            alignItems: "flex-start"
                                        }}>
                                            <div style={{
                                                flexShrink: 0,
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                backgroundColor: "#e7f5ff",
                                                border: "2px solid #00b8e6",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                color: "#00b8e6"
                                            }}>1</div>
                                            <div style={{ paddingTop: "4px" }}>
                                                <Trans i18nKey="register.scan1" components={{ 1: <strong /> }} />
                                            </div>
                                        </li>
                                        <li className="qrStep" style={{
                                            display: "flex",
                                            gap: "15px",
                                            marginBottom: "16px",
                                            alignItems: "flex-start"
                                        }}>
                                            <div style={{
                                                flexShrink: 0,
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                backgroundColor: "#e7f5ff",
                                                border: "2px solid #00b8e6",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                color: "#00b8e6"
                                            }}>2</div>
                                            <div style={{ paddingTop: "4px" }}>
                                                <Trans i18nKey="register.scan2" components={{ 1: <strong /> }} />
                                            </div>
                                        </li>
                                        <li className="qrStep" style={{
                                            display: "flex",
                                            gap: "15px",
                                            marginBottom: "16px",
                                            alignItems: "flex-start"
                                        }}>
                                            <div style={{
                                                flexShrink: 0,
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                backgroundColor: "#e7f5ff",
                                                border: "2px solid #00b8e6",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                color: "#00b8e6"
                                            }}>3</div>
                                            <div style={{ paddingTop: "4px" }}>
                                                {t("register.scan3")}
                                            </div>
                                        </li>
                                        <li className="qrStep" style={{
                                            display: "flex",
                                            gap: "15px",
                                            marginBottom: "0",
                                            alignItems: "flex-start"
                                        }}>
                                            <div style={{
                                                flexShrink: 0,
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                backgroundColor: "#fff3cd",
                                                border: "2px solid #ffb703",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                color: "#b26a00"
                                            }}>4</div>
                                            <div style={{ paddingTop: "4px" }}>
                                                {t("register.scan4")}
                                            </div>
                                        </li>
                                        <li className="qrStep" style={{
                                            display: "flex",
                                            gap: "15px",
                                            marginBottom: "0",
                                            alignItems: "flex-start"
                                        }}>
                                            <div style={{
                                                flexShrink: 0,
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                backgroundColor: "#e7f5ff",
                                                border: "2px solid #00b8e6",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                color: "#00b8e6"
                                            }}>5</div>
                                            <div style={{ paddingTop: "4px" }}>
                                                {t("register.scan5")}
                                            </div>
                                        </li>
                                    </ol>
                                </div>

                                {/* Right Column: QR Code */}
                                <div className="qrCodeSection" style={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <div className="qrCodeWrapper" style={{
                                        position: "relative",
                                        display: "inline-block",
                                        background: "#ffffff",
                                        padding: "12px",
                                        borderRadius: "8px",
                                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
                                    }}>
                                        <img 
                                            src={imageUrl} 
                                            alt={t("register.qrAlt")} 
                                            style={{
                                                display: "block",
                                                maxWidth: "240px",
                                                height: "auto",
                                                borderRadius: "4px"
                                            }} 
                                        />
                                        {/* WhatsApp Logo Watermark */}
                                        <div style={{
                                            position: "absolute",
                                            bottom: "18px",
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            width: "40px",
                                            height: "40px",
                                            backgroundColor: "#ffffff",
                                            borderRadius: "6px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                                            fontSize: "24px"
                                        }}>
                                            📱
                                        </div>
                                    </div>
                                    <p style={{
                                        marginTop: "20px",
                                        fontSize: "13px",
                                        color: "#666",
                                        textAlign: "center"
                                    }}>
                                        {t("register.internet")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <footer className="footer-registration" style={{
                backgroundColor: "#00B5E2",
                color: "#ffffff",
                padding: "20px 40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "40px",
                borderTop: "3px solid ",
                position: "relative"
            }}>
                <div style={{
                    position: "absolute",
                    left: "40px",
                    width: "45px",
                    height: "45px",
                    backgroundColor: "#ffffff",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#00B5E2"
                }}>
                    GU
                </div>
                <p style={{ margin: "0px", fontSize: "13px", color: "#ffffff" }}>
                    {t("common.footer", { year: new Date().getFullYear() })}
                </p>
            </footer>
        </div>
    );
};

export default RegistrationForm;
